/** @jsxImportSource theme-ui */
import React, { useEffect, useMemo } from 'react';
import { Card, Grid } from 'semantic-ui-react';

import * as dto from 'client/dto';
import { sxtheme } from 'utils/cssHelpers';
import { formatDate } from 'utils/dateTimeHelpers';
import { formatNumber } from 'utils/stringFormattingHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { Typography } from 'components/_common/Typography';
import { ReferenceListItem } from 'components/SidebarView/EventDetailPanel/ReferenceList';
import { TextTruncate } from 'components/TextTruncate';

type ActivityBlurbProps = {
  isGlobal?: boolean;
  isActive: boolean;
  isScrollToTarget: boolean;
  model: dto.DailyActivityModel;
};

export const ActivityBlurb: React.FC<ActivityBlurbProps> = ({
  isGlobal = false,
  model,
  isActive,
  isScrollToTarget,
}) => {
  const ref = React.useRef<HTMLDivElement>();
  const labelCases = isGlobal ? 'Global reported cases' : 'Reported cases';
  const labelDeaths = isGlobal ? 'Global reported deaths' : 'Reported deaths';
  const reportedCases = isGlobal
    ? model.caseCounts?.globalCaseCounts?.reportedCases
    : model.caseCounts?.locationCaseCounts?.caseCounts?.reportedCases;
  const reportedDeaths = isGlobal
    ? model.caseCounts?.globalCaseCounts?.deaths
    : model.caseCounts?.locationCaseCounts?.caseCounts?.deaths;

  const descriptionsAggregate = useMemo<dto.DescriptionReportModel[]>(
    () => (model.countryDescriptions || []).reduce((acc, x) => [...acc, ...x.descriptionList], []),
    [model]
  );
  const articlesAggregate = useMemo<dto.ArticleModel[]>(
    () => (descriptionsAggregate || []).reduce((acc, x) => acc.concat(x.articles || []), []),
    [descriptionsAggregate]
  );

  useEffect(() => {
    if (isScrollToTarget && ref && ref.current && ref.current.scrollIntoView)
      ref.current.scrollIntoView({ block: 'nearest' });
  }, [ref, isScrollToTarget]);

  return (
    <div
      ref={ref}
      className="ui fluid card"
      sx={{
        '&.ui.card': {
          ...(isActive && {
            border: `3px solid #C8D5E7`,
            margin: `-2px`,
            boxSizing: `content-box`,
          }),
        },
      }}
    >
      <Card.Content>
        <FlexGroup
          prefix={<BdIcon name="icon-calendar" color="stone50" />}
          alignItems="center"
          sx={{
            mb: '8px',
          }}
        >
          <Typography variant="h6" color="stone90">
            {formatDate(model.reportDate)}
          </Typography>
        </FlexGroup>
        <Grid columns={2} divided="vertically">
          <Grid.Row divided>
            <Grid.Column>
              <Typography variant="caption" color="deepSea50">
                <span sx={{ verticalAlign: 'middle', mr: '2px' }}>{labelCases}</span>
                {/* TODO: not sure if james will bring these back, keeping verticalAlign for now */}
                {/* <BdIcon name="icon-information" sx={{ verticalAlign: 'middle' }} /> */}
              </Typography>
              <Typography variant="subtitle2" color="stone90">
                {formatNumber(reportedCases, null, null, 'emdash-0')}
              </Typography>
            </Grid.Column>
            <Grid.Column>
              <Typography variant="caption" color="deepSea50">
                <span sx={{ verticalAlign: 'middle', mr: '2px' }}>{labelDeaths}</span>
                {/* <BdIcon name="icon-information" sx={{ verticalAlign: 'middle' }} /> */}
              </Typography>
              <Typography variant="subtitle2" color="stone90">
                {formatNumber(reportedDeaths, null, null, 'emdash-0')}
              </Typography>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {descriptionsAggregate?.length > 0 && (
          <div
            sx={{
              mt: '14px',
              pt: '24px',
              mb: '-4px',
              borderTop: sxtheme(t => `1px solid ${t.colors.stone20}`),
            }}
          >
            {descriptionsAggregate.map(({ locationName, description }) => (
              <div key={locationName} sx={{ mb: '20px' }}>
                <Typography variant="h6" color="deepSea90" marginBottom="4px">
                  {locationName}
                </Typography>
                <Typography variant="body2" color="stone90" marginBottom="4px">
                  <TextTruncate value={description} length={150} isExpanded={isActive} />
                </Typography>
              </div>
            ))}
          </div>
        )}
        {articlesAggregate && articlesAggregate.length > 0 && (
          <>
            <Typography variant="overline" color="deepSea90" sx={{ mb: '6px' }}>
              Sources
            </Typography>
            {articlesAggregate.map(article => (
              <div sx={{ mb: '10px' }}>
                <ReferenceListItem {...article} />
              </div>
            ))}
          </>
        )}
      </Card.Content>
    </div>
  );
};
